import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import { base_api_url } from "../../../environment.jsx";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import PropTypes from "prop-types";
import { X } from "feather-icons-react/build/IconComponents";
import "./AddMenu.css";

dayjs.extend(customParseFormat);

// Définition des constantes hors du composant pour la stabilité
const joursDeLaSemaine = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

const KeyDay = {
  Lundi: "dispo_lun",
  Mardi: "dispo_mar",
  Mercredi: "dispo_mer",
  Jeudi: "dispo_jeu",
  Vendredi: "dispo_ven",
  Samedi: "dispo_sam",
  Dimanche: "dispo_dim",
};

// État initial du formulaire pour les nouveaux menus
const initialState = {
  nom: "",
  description: "",
  heure_debut: null,
  heure_fin: null,
  dispo_lun: false,
  dispo_mar: false,
  dispo_mer: false,
  dispo_jeu: false,
  dispo_ven: false,
  dispo_sam: false,
  dispo_dim: false,
};

const AddMenu = ({
  show,
  onClose,
  editMode = false,
  menuData = null,
  onFormSubmit,
}) => {
  const ideresto = JSON.parse(localStorage.getItem("user"))?.restoId;
  const MySwal = withReactContent(Swal);
  const [loading, setLoading] = useState(false);
  const [joursSelectionnes, setJoursSelectionnes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [formData, setFormData] = useState({ ...initialState, id_restaurant: ideresto });

  // Synchronisation du formulaire en mode édition
  useEffect(() => {
    if (show) {
      if (editMode && menuData) {
        setFormData({
          nom: menuData.nom || "",
          description: menuData.description || "",
          heure_debut: menuData.heure_debut || "",
          heure_fin: menuData.heure_fin || "",
          dispo_lun: menuData.dispo_lun || false,
          dispo_mar: menuData.dispo_mar || false,
          dispo_mer: menuData.dispo_mer || false,
          dispo_jeu: menuData.dispo_jeu || false,
          dispo_ven: menuData.dispo_ven || false,
          dispo_sam: menuData.dispo_sam || false,
          dispo_dim: menuData.dispo_dim || false,
          id_menu: menuData.id_menu || null,
          id_restaurant: ideresto,
        });

        const selectedDays = [];
        Object.entries(KeyDay).forEach(([jour, key]) => {
          if (menuData[key]) selectedDays.push(jour);
        });
        setJoursSelectionnes(selectedDays);
        setSelectAll(selectedDays.length === joursDeLaSemaine.length);
      } else {
        setFormData({ ...initialState, id_restaurant: ideresto });
        setJoursSelectionnes([]);
        setSelectAll(false);
      }
    }
  }, [show, editMode, menuData, ideresto]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onTimeChange = (e, fieldName) => {
    const timeValue = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: timeValue || null,
    }));
  };

  const handleSelectAllChange = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      setJoursSelectionnes([...joursDeLaSemaine]);
      const updatedFormData = { ...formData };
      Object.values(KeyDay).forEach((key) => {
        updatedFormData[key] = true;
      });
      setFormData(updatedFormData);
    } else {
      setJoursSelectionnes([]);
      const updatedFormData = { ...formData };
      Object.values(KeyDay).forEach((key) => {
        updatedFormData[key] = false;
      });
      setFormData(updatedFormData);
    }
  };

  const handleCheckboxChange = (jour) => {
    const isSelected = !joursSelectionnes.includes(jour);
    const updatedSelection = isSelected
      ? [...joursSelectionnes, jour]
      : joursSelectionnes.filter((j) => j !== jour);

    setJoursSelectionnes(updatedSelection);

    const key = KeyDay[jour];
    if (key) {
      setFormData((prevData) => ({
        ...prevData,
        [key]: isSelected,
      }));
    }

    setSelectAll(updatedSelection.length === joursDeLaSemaine.length);
  };

  const resetForm = () => {
    setFormData({ ...initialState, id_restaurant: ideresto });
    setJoursSelectionnes([]);
    setSelectAll(false);
  };

  const route = all_routes;
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      let response;

      const formDatatoSubmit = {
        nom: formData.nom,
        description: formData.description,
        heure_debut: formData.heure_debut === "" ? null : (formData.heure_debut || null),
        heure_fin: formData.heure_fin === "" ? null : (formData.heure_fin || null),
        dispo_lun: formData.dispo_lun,
        dispo_mar: formData.dispo_mar,
        dispo_mer: formData.dispo_mer,
        dispo_jeu: formData.dispo_jeu,
        dispo_ven: formData.dispo_ven,
        dispo_sam: formData.dispo_sam,
        dispo_dim: formData.dispo_dim,
        id_restaurant: ideresto,
      };

      if (editMode && formData.id_menu) {
        formDatatoSubmit.id_menu = formData.id_menu;
        response = await axios.put(
          `${base_api_url}api/menu/${formData.id_menu}`,
          formDatatoSubmit,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          `${base_api_url}api/menu`,
          formDatatoSubmit,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      if (response.status === 200 || response.status === 201) {
        MySwal.fire({
          icon: "success",
          text: editMode
            ? "Le menu a été modifié avec succès."
            : "Le menu a été créé avec succès.",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "pixel-toast" },
        });

        onClose();
        if (onFormSubmit) onFormSubmit();

        if (!editMode) {
          localStorage.setItem("menu", JSON.stringify(response.data));
          setTimeout(() => {
            navigate(route.menus);
          }, 3000);
        }
      }
    } catch (error) {
      console.error(error);
      let errorMessage = "Une erreur s'est produite. Veuillez réessayer.";
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      }
      MySwal.fire({
        icon: "error",
        text: errorMessage,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        customClass: { popup: "pixel-toast" }
      });
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <div className="premium-menu-modal-overlay" onClick={onClose}>
      <div className="premium-menu-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="premium-menu-modal-header">
          <h4>{editMode ? "Modifier le menu" : "Ajouter un menu"}</h4>
          <button
            type="button"
            className="premium-modal-close-btn"
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit}>
          <div className="premium-menu-modal-body">
            <div className="premium-form-group">
              <label className="premium-form-label">
                NOM DU MENU <span className="required-star">*</span>
              </label>
              <input
                type="text"
                className="premium-input-style"
                placeholder="Entrez le nom du menu"
                value={formData.nom}
                name="nom"
                onChange={handleChange}
                required
              />
            </div>

            <div className="premium-form-group">
              <label className="premium-form-label">DESCRIPTION</label>
              <textarea
                className="premium-input-style premium-textarea-style"
                placeholder="Saisissez une description..."
                name="description"
                value={formData.description}
                onChange={handleChange}
              ></textarea>
            </div>

            <div className="premium-form-group">
              <label className="premium-form-label">JOUR(S) DISPONIBLE:</label>

              <div className="select-all-box" onClick={handleSelectAllChange}>
                <input
                  type="checkbox"
                  className="premium-checkbox-custom"
                  checked={selectAll}
                  readOnly
                />
                <span className="select-all-text">
                  {selectAll ? "Désélectionner tous les jours" : "Sélectionner tous les jours"}
                </span>
              </div>

              <div className="days-checkbox-grid">
                {joursDeLaSemaine.map((jour) => (
                  <div
                    key={jour}
                    className={`day-checkbox-item ${joursSelectionnes.includes(jour) ? 'active' : ''}`}
                    onClick={() => handleCheckboxChange(jour)}
                  >
                    <input
                      type="checkbox"
                      className="premium-checkbox-custom"
                      checked={joursSelectionnes.includes(jour)}
                      readOnly
                      style={{ marginRight: '8px', width: '16px', height: '16px' }}
                    />
                    <span className="day-name-text">{jour}</span>
                  </div>
                ))}
              </div>

              <small style={{ color: '#94a3b8', fontSize: '12px', marginTop: '12px', display: 'block' }}>
                {joursSelectionnes.length > 0
                  ? `${joursSelectionnes.length} jour(s) sélectionné(s)`
                  : "Aucun jour sélectionné"}
              </small>
            </div>

            <div className="premium-time-row">
              <div className="time-field-wrapper">
                <label className="premium-form-label">HEURE DÉBUT</label>
                <input
                  type="time"
                  className="premium-input-style"
                  onChange={(e) => onTimeChange(e, "heure_debut")}
                  name="heure_debut"
                  value={formData.heure_debut || ""}
                />
              </div>
              <div className="time-field-wrapper">
                <label className="premium-form-label">HEURE FIN</label>
                <input
                  type="time"
                  className="premium-input-style"
                  onChange={(e) => onTimeChange(e, "heure_fin")}
                  name="heure_fin"
                  value={formData.heure_fin || ""}
                />
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="premium-menu-modal-footer">
            <button type="submit" className="btn-premium-save" disabled={loading}>
              {loading ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : null}
              Enregistrer
            </button>
            <button
              type="button"
              className="btn-premium-cancel"
              onClick={() => {
                onClose();
              }}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

AddMenu.propTypes = {
  onFormSubmit: PropTypes.func,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  editMode: PropTypes.bool,
  menuData: PropTypes.object,
};

export default AddMenu;
